import pagesData from '../pages.json';

// ============================================================================
// Shared navigation eligibility
// ----------------------------------------------------------------------------
// Single source of truth for "which pages appear in the primary navigation".
// Consumed by Header (renders the nav) and SitemapContent (mirrors the nav as
// a link list), so the two never drift. See buildNavLinks / getEligibleNavPages
// below.
// ============================================================================

export interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

export interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

/** Page aliases that should never appear in the main navigation.
    Apply Now, Resident Login (resident-access), and Contact Us are
    intentionally NOT excluded — they're surfaced as primary nav links.
    Schedule a Tour is excluded from the link row because it is promoted to
    the standalone CTA button on the right. Pet Policy lives in the footer /
    sitemap rather than the top nav. */
export const NAV_EXCLUDED_ALIASES = new Set<string>([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'thank-you',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
  'pet-policy',
]);

/** Normalize a pathname for active-link comparison: drop the query/hash and
    trailing slashes so `/floor-plans`, `/floor-plans/`, and
    `/floor-plans?x=1` all match the `/floor-plans` nav href. Root stays `/`. */
export function normalizePath(p: string): string {
  const path = (p || '').split(/[?#]/)[0];
  return path.replace(/\/+$/, '') || '/';
}

/**
 * Flat, position-sorted list of `[alias, page]` entries eligible for the
 * primary navigation. This is the canonical "pages in navigation" filter:
 * excluded aliases, hidden pages, and template pages are dropped, and the
 * Virtual Tour / Interactive Map pages appear only when the property has the
 * data to back them (see the flags passed from the caller).
 */
export function getEligibleNavPages(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
): Array<[string, PageEntry]> {
  return (Object.entries(pagesData as Record<string, PageEntry>) as Array<[string, PageEntry]>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);
}

/**
 * Build navigation links from pages.json. Only includes eligible pages
 * (see getEligibleNavPages), sorted by position. Pages declaring
 * `parent: <alias>` are nested as `children` of their parent entry rather than
 * appearing at the top level. Orphaned children (parent missing or hidden)
 * fall back to the top level so a page is never silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the Interactive
 * Map page — that page renders the ApartmentSync (Repli360 / RRAC)
 * "interactive leasing view" widget, keyed by two per-property custom fields
 * (`custom_apartmentsync_javascript_id` and `custom_apartmentsync_config_id`).
 * A property without both is a half-configured / no-real-map state, so we drop
 * the page from the nav (the SSG also marks the page hidden + noindex — see
 * ssg-entry.ts).
 */
export function buildNavLinks(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
): NavLink[] {
  const eligible = getEligibleNavPages(hasVirtualTours, hasInteractiveLeasing);
  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  // Second pass: attach children to parents, preserving sort order. Pages
  // whose parent isn't eligible (hidden / excluded / missing) bubble up to
  // the top level so we never lose a page from the nav.
  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}
